import { useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import ArrowRightSmall from "../../assets/img/arrow-right-small.svg?react";

/**
 * Shared building blocks for the Activity Details modal (Figma: User Activity Logs).
 *
 * Every action type — ARI, Promo, Res Report, User Management — is the same four pieces
 * in a different arrangement: the info grid, an optional "Applied To" section, a "Change
 * Log" section, and rows that are either a plain value or a before/after diff. They live
 * here rather than inside ActivityDetailsModal so the remaining sections can be built by
 * composing them instead of repeating the markup.
 */

/** Info grid — paired label/value cells; a row of one cell spans the full width. */
export const InfoGrid = ({ rows }) => (
  <div className="ua-info-grid">
    {rows.map((cells, i) => (
      <div
        className="ua-info-grid__row"
        key={cells.map((c) => c.label).join("|") || i}
      >
        {cells.map((cell) => (
          <div className="ua-info-grid__pair" key={cell.label}>
            <div className="ua-info-grid__label">{cell.label}</div>
            <div className="ua-info-grid__value">{cell.value}</div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

InfoGrid.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, value: PropTypes.node })
    )
  ).isRequired,
};

/** A titled section ("Applied To", "Change Log") wrapping a bordered table. */
export const DetailSection = ({ title, columns, children }) => (
  <section className="ua-section">
    {title && <h4 className="ua-section__title">{title}</h4>}
    <div className="ua-section__table">
      {columns && (
        <div className="ua-section__head">
          <div className="ua-section__head-label" />
          {columns.map((c, i) => (
            <div
              // The first cell ("From") carries the arrow-column offset. A modifier rather than
              // :first-of-type, which matches by TAG: the label div above is the first div, so
              // the CSS selector never matched a head-cell and the offset was never applied.
              className={
                i === 0
                  ? "ua-section__head-cell ua-section__head-cell--from"
                  : "ua-section__head-cell"
              }
              key={c}
            >
              {c}
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  </section>
);

DetailSection.propTypes = {
  title: PropTypes.node,
  /** Column headers (e.g. From / To). Omitted when the section has no diff rows. */
  columns: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
};

/** label | value. `tone` colours the value for a single-sided change (e.g. "Re-pushed"). */
export const ValueRow = ({ label, value, tone }) => (
  <div className="ua-row">
    <div className="ua-row__label">{label}</div>
    <div
      className={cx("ua-row__value", {
        "ua-row__value--mono": Boolean(tone),
        [`ua-row__value--${tone}`]: Boolean(tone),
      })}
    >
      {value}
    </div>
  </div>
);

ValueRow.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  tone: PropTypes.oneOf(["added", "removed"]),
};

/**
 * A list value that collapses to its first entry plus a "+N" chip, expanding to the full
 * comma-separated list. Used wherever a row can carry many things — ARI's Room Types,
 * User Management's Properties Assigned, Promo's Properties / Rate Plans / Rooms — so a
 * user with fifty properties doesn't get a fifty-line row by default.
 *
 * Each row keeps its own state, matching the design: one row can be expanded while its
 * neighbours stay collapsed. A real <button> so it is keyboard-reachable — the design
 * draws it as a plain underlined link, which the styling reproduces.
 */
export const ListValue = ({ items, tone }) => {
  const [expanded, setExpanded] = useState(false);
  const list = (items || []).filter(
    (v) => v !== null && v !== undefined && v !== ""
  );

  // Wrapped rather than returned bare so every branch returns an element: a component that
  // sometimes returns a string and sometimes JSX renders the same but reads as two
  // different functions, and callers cannot tell which they will get.
  if (list.length === 0) return <>{"\u2014"}</>;
  if (list.length === 1) return <>{String(list[0])}</>;

  const rest = list.length - 1;
  return (
    <span className="ua-list">
      <span
        className={cx("ua-list__text", {
          [`ua-row__value--${tone}`]: Boolean(tone),
        })}
      >
        {expanded ? list.join(", ") : String(list[0])}
      </span>
      {!expanded && (
        <span className="ua-list__chip" aria-hidden="true">
          +{rest}
        </span>
      )}
      <button
        type="button"
        className="ua-list__toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? "show less" : "show all"}
      </button>
    </span>
  );
};

ListValue.propTypes = {
  items: PropTypes.array,
  tone: PropTypes.oneOf(["added", "removed"]),
};

/**
 * label | before → after. The arrow is the design's affordance for "this changed"; it
 * carries no text, so it is hidden from assistive tech and the change is announced by
 * the labelled before/after cells instead.
 */
export const DiffRow = ({ label, before, after }) => (
  <div className="ua-row ua-row--diff">
    <div className="ua-row__label">{label}</div>
    <div className="ua-row__value ua-row__value--mono ua-row__value--removed">
      {before}
    </div>
    <div className="ua-row__arrow" aria-hidden="true">
      <span className="ua-row__arrow-dot">
        <ArrowRightSmall />
      </span>
    </div>
    <div className="ua-row__value ua-row__value--mono ua-row__value--added">
      {after}
    </div>
  </div>
);

DiffRow.propTypes = {
  label: PropTypes.node,
  before: PropTypes.node,
  after: PropTypes.node,
};
